import React from "react";
import { TablePagination, Typography, makeStyles } from "@material-ui/core";
import TablePaginationActions from "widgets/Tables/Pagination";

const useStyles = makeStyles((theme) => ({
  panel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    overflow: "hidden",
  },
  panelTitle: {
    color: "#f5f7fb",
    fontSize: 17,
    fontWeight: 900,
    padding: theme.spacing(2, 2.5),
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    borderCollapse: "collapse",
    minWidth: 760,
    width: "100%",
  },
  tableHead: {
    background: "#1a253b",
  },
  headerCell: {
    color: "#53647e",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1,
    padding: theme.spacing(1.35, 2),
    textAlign: "left",
    textTransform: "uppercase",
  },
  headerCellRight: {
    textAlign: "right",
  },
  bodyRow: {
    borderTop: "1px solid #1d3454",
  },
  bodyCell: {
    color: "#acd0f6",
    fontSize: 13,
    padding: theme.spacing(1.7, 2),
  },
  bodyCellRight: {
    textAlign: "right",
  },
  statusText: {
    color: "#b8cdf0",
    fontSize: 13,
    padding: theme.spacing(0, 2.5, 2),
  },
  errorText: {
    color: "#ff8a9a",
    fontSize: 13,
    padding: theme.spacing(0, 2.5, 2),
  },
  emptyCell: {
    color: "#53647e",
    fontSize: 13,
    padding: theme.spacing(3, 2),
    textAlign: "center",
  },
  pagination: {
    alignItems: "center",
    background: "#111a2b",
    borderTop: "1px solid #1d3454",
    color: "#acd0f6",
    display: "flex",
    justifyContent: "flex-end",
    minHeight: 52,
    "& .MuiTablePagination-toolbar": {
      minHeight: 52,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
    },
    "& .MuiTablePagination-caption": {
      color: "#acd0f6",
      fontSize: 12,
    },
    "& .MuiTablePagination-selectIcon": {
      color: "#acd0f6",
    },
    "& .MuiSelect-root": {
      color: "#f5f7fb",
      fontSize: 12,
    },
  },
}));

/**
 * Tabla genérica para el módulo contable.
 *
 * @param {string}   title            Título del panel
 * @param {Array}    columns          Definición de columnas:
 *                                     { key, label, render(row), align? }
 * @param {Array}    rows             Filas de la página actual (ya paginadas por el API)
 * @param {number}   totalElements    Total de registros en el servidor
 * @param {number}   page             Página actual (0-indexed)
 * @param {number}   rowsPerPage      Filas por página
 * @param {Function} onPageChange     Handler (event, newPage)
 * @param {Function} onRowsPerPageChange Handler (event)
 * @param {boolean}  loading
 * @param {any}      error
 * @param {string}   emptyMessage     Texto cuando no hay datos
 * @param {string}   loadingMessage   Texto mientras carga
 * @param {Array}    rowsPerPageOptions
 */
const ContableTable = ({
  title,
  columns = [],
  rows = [],
  totalElements = 0,
  page = 0,
  rowsPerPage = 15,
  onPageChange,
  onRowsPerPageChange,
  loading = false,
  error = null,
  emptyMessage = "No existen registros para mostrar.",
  loadingMessage = "Cargando...",
  rowsPerPageOptions = [5, 10, 15, 25],
}) => {
  const classes = useStyles();

  return (
    <div className={classes.panel}>
      {title && (
        <Typography className={classes.panelTitle}>{title}</Typography>
      )}

      {loading && (
        <Typography className={classes.statusText}>{loadingMessage}</Typography>
      )}
      {error && (
        <Typography className={classes.errorText}>
          No se pudieron cargar los datos.
        </Typography>
      )}

      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          <thead className={classes.tableHead}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`${classes.headerCell} ${
                    col.align === "right" ? classes.headerCellRight : ""
                  }`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                className={classes.bodyRow}
                key={row.idAsignacionComprobanteElectronico ?? index}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`${classes.bodyCell} ${
                      col.align === "right" ? classes.bodyCellRight : ""
                    }`}
                  >
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr className={classes.bodyRow}>
                <td
                  className={classes.emptyCell}
                  colSpan={columns.length || 1}
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        ActionsComponent={TablePaginationActions}
        className={classes.pagination}
        component="div"
        count={totalElements}
        labelDisplayedRows={({ from, to, count }) =>
          count ? `${from}-${to} de ${count}` : "0 de 0"
        }
        labelRowsPerPage="Filas por página:"
        onChangePage={onPageChange}
        onChangeRowsPerPage={onRowsPerPageChange}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
      />
    </div>
  );
};

export default ContableTable;
